import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';

interface UseResizableSidePanelOptions {
    isOpen: boolean;
    defaultPanelWidth?: number;
    minPanelWidth?: number;
    maxPanelWidth?: number;
    minMainWidth?: number;
}

const MOBILE_BREAKPOINT = 768;

export const useResizableSidePanel = ({
    isOpen,
    defaultPanelWidth = 420,
    minPanelWidth = 120,
    maxPanelWidth,
    minMainWidth = 120,
}: UseResizableSidePanelOptions) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [panelWidth, setPanelWidth] = useState<number>(defaultPanelWidth);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [viewportWidth, setViewportWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);

    const isDesktop = viewportWidth >= MOBILE_BREAKPOINT;

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (event: MouseEvent) => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const rawPanelWidth = rect.right - event.clientX;
            const maxWidthByContainer = Math.max(minPanelWidth, rect.width - minMainWidth);
            const maxAllowedWidth = typeof maxPanelWidth === 'number'
                ? Math.min(maxPanelWidth, maxWidthByContainer)
                : maxWidthByContainer;
            const clampedPanelWidth = Math.min(Math.max(rawPanelWidth, minPanelWidth), maxAllowedWidth);

            setPanelWidth(clampedPanelWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, maxPanelWidth, minMainWidth, minPanelWidth]);

    const dividerStyle: CSSProperties = useMemo(() => ({
        cursor: 'col-resize',
    }), []);

    const mainStyle: CSSProperties = useMemo(() => {
        if (!isDesktop || !isOpen) {
            return { width: '100%' };
        }

        return {
            width: `calc(100% - ${panelWidth}px - 12px)`,
        };
    }, [isDesktop, isOpen, panelWidth]);

    const panelStyle: CSSProperties = useMemo(() => {
        if (!isDesktop || !isOpen) {
            return { width: '100%' };
        }

        return { width: `${panelWidth}px` };
    }, [isDesktop, isOpen, panelWidth]);

    return {
        containerRef,
        isDesktop,
        isResizing,
        setIsResizing,
        mainStyle,
        panelStyle,
        dividerStyle,
    };
};

export default useResizableSidePanel;
